import { useState, useEffect, useCallback, useRef } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'
import { UserStats, UserEvent, RecentUserCounts, MonthProgress } from './types'

const DEFAULT_STATS: UserStats = {
  totalUsers: 0,
  premiumUsers: 0,
  premiumMonthly: 0,
  premiumYearly: 0,
  yearlyThisMonth: 0,
  monthlyThisMonth: 0,
  yearlyRenewalsThisMonth: 0,
  avgMonthlyAcquisition: 0,
  avgYearlyAcquisition: 0,
  monthsOfData: 1,
  monthProgress: undefined,
  recentUsers: {
    last10Min: 0,
    last30Min: 0,
    last1Hour: 0,
    last24Hours: 0,
  },
  userGrowthData: [],
  revenueHistory: [],
  totalScans: 0,
  totalReviews: 0,
  totalBrands: 0,
  totalProducts: 0,
}

// Poll every 2 seconds
const POLL_INTERVAL = 2000

// Local cache for instant page loads
const STORAGE_KEY = 'nexus_user_stats_cache'
const LOCAL_CACHE_TTL_MS = 2 * 60 * 1000 // 2 minutes - shorter since this is live data

interface CachedStats {
  stats: UserStats
  timestamp: number
}

// Get cached stats from localStorage
function getLocalCache(): CachedStats | null {
  try {
    const cached = localStorage.getItem(STORAGE_KEY)
    if (cached) {
      const parsed = JSON.parse(cached) as CachedStats
      // Check if cache is fresh enough
      if (Date.now() - parsed.timestamp < LOCAL_CACHE_TTL_MS) {
        return parsed
      }
    }
  } catch (e) {
    console.warn('Failed to read user stats cache:', e)
  }
  return null
}

// Save stats to localStorage
function setLocalCache(stats: UserStats): void {
  try {
    const cached: CachedStats = {
      stats,
      timestamp: Date.now(),
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cached))
  } catch (e) {
    console.warn('Failed to write user stats cache:', e)
  }
}

interface UseUserStatsReturn extends UserStats {
  events: UserEvent[]
  isConnected: boolean
  connectionError: string | null
  eventCounter: number
}

export function useUserStats(client: SupabaseClient | null): UseUserStatsReturn {
  // Initialize with cached data for instant page load
  const [stats, setStats] = useState<UserStats>(() => {
    const cached = getLocalCache()
    return cached?.stats || DEFAULT_STATS
  })
  const [events, setEvents] = useState<UserEvent[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [eventCounter, setEventCounter] = useState(0)

  // Track last known state to detect changes
  const lastKnownTotal = useRef(0)
  const lastKnownPremium = useRef(0)
  const isInitialMount = useRef(true)
  const eventIndexRef = useRef(0)
  const hasInitializedFromCache = useRef(false)
  // Track seen user IDs to prevent duplicate events
  const seenUserIds = useRef<Set<string>>(new Set())
  const seenPremiumIds = useRef<Set<string>>(new Set())

  // Edge Function URL from env (for Buy'r org)
  // TODO: In future, store per-org edge function URLs in the organizations table
  const edgeFunctionUrl = client
    ? `${import.meta.env.VITE_BUYR_EDGE_FUNCTIONS_URL}/admin-stats`
    : null

  const anonKey = import.meta.env.VITE_BUYR_SUPABASE_ANON_KEY

  const addEvent = useCallback((event: Omit<UserEvent, 'eventIndex'>) => {
    const newIndex = ++eventIndexRef.current
    setEvents((prev) => {
      const newEvents = [{ ...event, eventIndex: newIndex }, ...prev].slice(0, 50)
      return newEvents
    })
    setEventCounter(newIndex)
  }, [])

  const checkForUpdates = useCallback(async () => {
    if (!client || !edgeFunctionUrl) return

    try {
      // Call the Edge Function instead of querying tables directly
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          lastTotal: lastKnownTotal.current,
          lastPremium: lastKnownPremium.current,
        }),
      })

      if (!response.ok) {
        throw new Error(`Edge function error: ${response.status}`)
      }

      const data = await response.json()

      const currentTotalUsers = data.totalUsers || 0
      const currentPremiumUsers = data.premiumUsers || 0

      // Build the new stats object
      const newStats: UserStats = {
        totalUsers: currentTotalUsers,
        premiumUsers: currentPremiumUsers,
        premiumMonthly: data.premiumMonthly || 0,
        premiumYearly: data.premiumYearly || 0,
        yearlyThisMonth: data.yearlyThisMonth || 0,
        monthlyThisMonth: data.monthlyThisMonth || 0,
        yearlyRenewalsThisMonth: data.yearlyRenewalsThisMonth || 0,
        avgMonthlyAcquisition: data.avgMonthlyAcquisition || 0,
        avgYearlyAcquisition: data.avgYearlyAcquisition || 0,
        monthsOfData: data.monthsOfData || 1,
        monthProgress: data.monthProgress || undefined,
        recentUsers: data.recentUsers || DEFAULT_STATS.recentUsers,
        userGrowthData: data.userGrowthData || [],
        revenueHistory: data.revenueHistory || [],
        totalScans: data.totalScans || 0,
        totalReviews: data.totalReviews || 0,
        totalBrands: data.totalBrands || 0,
        totalProducts: data.totalProducts || 0,
      }

      // Update stats and cache
      setStats(newStats)
      setLocalCache(newStats)

      // Skip event generation on initial mount
      if (isInitialMount.current) {
        isInitialMount.current = false
        lastKnownTotal.current = currentTotalUsers
        lastKnownPremium.current = currentPremiumUsers
        setIsConnected(true)
        return
      }

      // Process new users from Edge Function response (with deduplication)
      if (data.newUsers && data.newUsers.length > 0) {
        data.newUsers.reverse().forEach((user: any) => {
          // Skip if we've already seen this user ID
          if (seenUserIds.current.has(user.id)) return
          seenUserIds.current.add(user.id)

          addEvent({
            id: user.id,
            type: 'new_user',
            timestamp: user.created_at || new Date().toISOString(),
            email: user.email,
            name: user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : undefined,
          })
        })
        lastKnownTotal.current = currentTotalUsers

        // Limit set size to prevent memory leak (keep last 1000)
        if (seenUserIds.current.size > 1000) {
          const arr = Array.from(seenUserIds.current)
          seenUserIds.current = new Set(arr.slice(-500))
        }
      }

      // Process premium upgrades from Edge Function response (with deduplication)
      if (data.premiumUpgrades && data.premiumUpgrades.length > 0) {
        data.premiumUpgrades.reverse().forEach((user: any) => {
          // Skip if we've already seen this premium upgrade
          if (seenPremiumIds.current.has(user.id)) return
          seenPremiumIds.current.add(user.id)

          addEvent({
            id: user.id,
            type: 'premium_upgrade',
            timestamp: new Date().toISOString(),
            email: user.email,
            name: user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : undefined,
            billingPeriod: user.billing_period,
          })
        })
        lastKnownPremium.current = currentPremiumUsers

        // Limit set size to prevent memory leak
        if (seenPremiumIds.current.size > 1000) {
          const arr = Array.from(seenPremiumIds.current)
          seenPremiumIds.current = new Set(arr.slice(-500))
        }
      }

      if (!isConnected) {
        setIsConnected(true)
        setConnectionError(null)
      }
    } catch (error) {
      console.error('Error polling for user updates:', error)
      setConnectionError('Failed to fetch updates')
      setIsConnected(false)
    }
  }, [client, edgeFunctionUrl, addEvent, isConnected])

  useEffect(() => {
    if (!client) return

    // Reset state when client changes
    isInitialMount.current = true

    // If we have cached data, initialize refs from it to avoid false events
    const cached = getLocalCache()
    if (cached?.stats) {
      lastKnownTotal.current = cached.stats.totalUsers
      lastKnownPremium.current = cached.stats.premiumUsers
      hasInitializedFromCache.current = true
      console.log(`UserStats: Using cached data (${Math.round((Date.now() - cached.timestamp) / 1000)}s old)`)
    } else {
      lastKnownTotal.current = 0
      lastKnownPremium.current = 0
      hasInitializedFromCache.current = false
    }

    const intervalId = setInterval(checkForUpdates, POLL_INTERVAL)
    checkForUpdates()

    return () => clearInterval(intervalId)
  }, [client, checkForUpdates])

  return {
    ...stats,
    events,
    isConnected,
    connectionError,
    eventCounter,
  }
}
